import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import PullToRefresh from './components/PullToRefresh';
import GoogleOAuthButton from './components/GoogleOAuthButton';
import { getApiUrl } from './config';
import { isTokenValid } from './utils/auth';

// Home-page feature cards. Icons are heroicons outline paths.
const FEATURES = [
  {
    title: 'Documents in, audio out',
    body:
      'Upload a PDF or an EPUB. Dissona splits it into chapters and narrates each one, so a book you never got round to becomes something you can start on the way to work.',
    icon:
      'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z',
  },
  {
    title: 'For the hours your hands are busy',
    body:
      'Play in the browser or on your phone. Dissona remembers where you stopped, so the drive home picks up where the walk left off.',
    icon: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z',
  },
  {
    title: 'Your agent can fill your library',
    body:
      'Connect Dissona to Claude, ChatGPT or any MCP client. Ask your agent to research a topic or draft a brief, and it lands in your library ready to listen to.',
    icon:
      'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z',
  },
  {
    title: 'The gist first',
    body:
      'Every chapter comes with a summary and its key points, so you can skim by ear and decide what deserves the full listen.',
    icon: 'M13 10V3L4 14h7v7l9-11h-7z',
  },
  {
    title: 'Or listen where you already listen',
    body:
      'Each project has its own private feed. Subscribe in Apple Podcasts, Overcast, Pocket Casts or Castro and it shows up with everything else you follow.',
    icon: 'M6 5c7.18 0 13 5.82 13 13M6 11a7 7 0 017 7m-6 0a1 1 0 11-2 0 1 1 0 012 0z',
  },
  {
    title: 'Pass it on',
    body:
      'Share a project with someone else. They accept the invitation and it appears in their library, in their ears, in their own time.',
    icon:
      'M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z',
  },
];

function Home() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    reason: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Background video, with the still image as the fallback. Skipped outright
  // for reduced-motion and data-saver users; dropped later if the file fails to
  // load or the browser refuses to autoplay it.
  const videoRef = useRef(null);
  const [playVideo, setPlayVideo] = useState(
    () =>
      !window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches &&
      !navigator.connection?.saveData,
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!playVideo || !video) return;
    // Set muted on the element itself: autoplay is only allowed for muted
    // video, and React has historically been unreliable about the attribute.
    video.muted = true;
    video.play?.()?.catch(() => setPlayVideo(false));
  }, [playVideo]);

  // Redirect logged-in mobile users to dashboard
  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile && isTokenValid()) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(
        getApiUrl('waitlist'),
        {
          email: formData.email,
          name: formData.name || null,
          reason: formData.reason || null,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      setSuccess("Thank you for your interest! We'll be in touch soon.");
      setFormData({ email: '', name: '', reason: '' });

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowModal(false);
        setSuccess('');
      }, 2000);
    } catch (err) {
      if (err.response) {
        setError(
          err.response.data.detail ||
            err.response.data.message ||
            'Failed to join waitlist. Please try again.',
        );
      } else if (err.request) {
        setError('Network error: Could not connect to server');
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ email: '', name: '', reason: '' });
    setError('');
    setSuccess('');
  };

  const handleRefresh = useCallback(async () => {
    window.location.reload();
  }, []);

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden">
        {/* Header */}
        <Navigation />

        {/* Main Content */}
        <main className="flex-grow">
          {/* Hero Section */}
          <section className="relative overflow-hidden py-20 sm:py-24 lg:py-32">
            {/* Base layer — also what shows whenever the video cannot play. */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: 'url("./home-image4.png")' }}
            />
            {playVideo && (
              <video
                ref={videoRef}
                className="absolute inset-0 h-full w-full object-cover"
                src={`${process.env.PUBLIC_URL}/homepage.mp4`}
                poster="./home-image4.png"
                autoPlay
                muted
                loop
                playsInline
                aria-hidden="true"
                tabIndex={-1}
                onError={() => setPlayVideo(false)}
                data-testid="hero-video"
              />
            )}
            {/* The wash that keeps the copy readable, above whichever is behind it. */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(17, 17, 33, 0.8) 0%, rgba(17, 17, 33, 0.9) 100%)',
              }}
            />
            <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
              <div className="flex flex-col items-center gap-6">
                <h1 className="text-4xl font-bold tracking-tighter text-white sm:text-5xl md:text-6xl">
                  Read it with your ears
                </h1>
                <p className="max-w-2xl text-lg text-gray-300 dark:text-gray-400">
                  Dissona turns what you were going to read into something you can
                  listen to. Keep your books, papers and reports in one library and
                  play them on your laptop, your phone or your podcast app — while
                  you drive, walk, or do the dishes.
                </p>
                <button
                  onClick={handleOpenModal}
                  className="mt-4 flex h-12 items-center justify-center rounded-lg bg-primary px-6 text-base font-bold text-white hover:bg-primary/90 transition-colors"
                >
                  <span className="truncate">Join Private Beta</span>
                </button>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="py-16 sm:py-20 lg:py-24 bg-background-light dark:bg-background-dark">
            <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
              <div className="mb-12 text-center">
                <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
                  Your reading list, on tap
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-400">
                  Put content in once — by hand or through your AI agent — and reach
                  it from anywhere, in the format that fits the moment.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                {FEATURES.map(({ title, body, icon }) => (
                  <div
                    key={title}
                    className="flex flex-col gap-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-background-dark/50 p-6 transition-shadow hover:shadow-lg dark:hover:border-primary/50"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <svg
                        className="h-7 w-7"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d={icon}
                        />
                      </svg>
                    </div>
                    <div className="flex flex-col gap-1">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <Footer />

        {/* Beta Access Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="relative mx-4 w-full max-w-md rounded-lg bg-white dark:bg-background-dark border border-gray-200 dark:border-gray-800 p-6 shadow-xl">
              {/* Close button */}
              <button
                onClick={handleCloseModal}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Modal content */}
              <h2 className="mb-4 text-2xl font-bold text-gray-900 dark:text-white">
                Join Private Beta
              </h2>
              <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
                Dissona is in private beta. Leave your email and we will let you
                know when there is room for you.
              </p>

              {/* Success message */}
              {success && (
                <div className="mb-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3">
                  <p className="text-sm text-green-800 dark:text-green-200">
                    {success}
                  </p>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    {error}
                  </p>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="reason"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Note
                  </label>
                  <textarea
                    id="reason"
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    disabled={loading}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed resize-none"
                    placeholder="Tell us what got you interested or just leave us a note"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    disabled={loading}
                    className="flex-1 h-10 rounded-lg border border-gray-300 dark:border-gray-600 px-4 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 h-10 rounded-lg bg-primary px-4 text-sm font-bold text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white dark:bg-background-dark px-2 text-gray-500 dark:text-gray-400">
                    Or
                  </span>
                </div>
              </div>

              {/* Google OAuth Button */}
              <GoogleOAuthButton
                flowType="waitlist"
                text="Join with Google"
                intent="signup"
              />
            </div>
          </div>
        )}
      </div>
    </PullToRefresh>
  );
}

export default Home;
